import * as React from 'react'
import { Button, Card } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { PositionNumber, PositionNumberType } from 'api/common/constants'
import { UniLevelListChild } from 'api/common/types'
import { referrerGraphUniLevelListAPI } from 'api/referrerGraph'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputFieldConfirm from 'components/InputFieldConfirm'
import InputSelectField from 'components/InputSelectField'
import Pagination from 'components/Pagination'
import loginData from 'utilities/loginData'
import { ReferrerGraph, referrerGraphs, KeyValue, positions, getPositionLabel } from 'utilities/resources/options'
import { autoCast, numberFormat } from 'utilities/typeUtility'

type Params = {
  positionNumber: string
  positionId: string
  pagination: string
}

/**
 * 紹介者管理ユニレベルリスト
 */
const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams<Params>()
  const positionNumber = autoCast<PositionNumberType>(params?.positionNumber ?? PositionNumber.MainPosition)
  const positionId = Number(params?.positionId ?? 0)
  const pagination = Number(params?.pagination ?? 0)
  const [selectablePositions, setSelectablePositions] = React.useState<KeyValue[]>([])
  const [positionNumberLabel, setPositionNumberLabel] = React.useState<string>()
  const [targetPositionId, setTargetPositionId] = React.useState<number>()
  const [selectedPositionId, setSelectedPositionId] = React.useState<number>()
  const [totalEarnings, setTotalEarnings] = React.useState<number>()
  const [registrationCountThisMonth, setRegistrationCountThisMonth] = React.useState<number>()
  const [uniLevelListChildren, setUniLevelListChildren] = React.useState<UniLevelListChild[]>([])
  const [maxCount, setMaxCount] = React.useState<number>(0)
  const changeGraph = (value: string) => {
    switch (value) {
      case ReferrerGraph.BinaryMap: {
        navigate(`/referrerManagementBinaryMap/${positionNumber}/0`)
        break
      }
      case ReferrerGraph.PersonalGroupList: {
        navigate(`/referrerManagementPersonalGroupList/${positionNumber}/-/1`)
        break
      }
    }
  }
  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
    } else {
      setPositionNumberLabel(getPositionLabel(positionNumber))
      referrerGraphUniLevelListAPI(autoCast(positionNumber), positionId, pagination, (response) => {
        if (response) {
          setSelectablePositions(positions.filter(({ key }) => response.selectablePositions.includes(autoCast(key))))
          setTargetPositionId(response.targetPositionId)
          setSelectedPositionId(response.selectedPositionId)
          setUniLevelListChildren(response.children)
          setTotalEarnings(response.totalAmount)
          setRegistrationCountThisMonth(response.registrationCountThisMonth)
          setMaxCount(response.pageCount)
        }
      })
    }
  }, [navigate, positionNumber, positionId, pagination])
  return (
    <>
      <Header title="紹介者管理" />
      <main>
        <section className="mx-4 my-5">
          <InputSelectField name="view-type" label="種別選択" items={referrerGraphs} bold setValue={changeGraph} value={ReferrerGraph.UniLevelList} />
          <InputSelectField
            name="position"
            label="ポジション選択"
            items={selectablePositions}
            bold
            setValue={(value) => navigate(`/referrerManagementUniLevelList/${value}/0/1`)}
          />
        </section>
        <section className="mx-4 my-5">
          <InputFieldConfirm label={`${positionNumberLabel}のコネクターID`} value={targetPositionId ?? '-'} bold />
          {targetPositionId !== selectedPositionId && <InputFieldConfirm label="選択中のコネクターID" value={selectedPositionId ?? '-'} bold />}
          {targetPositionId === selectedPositionId && <InputFieldConfirm label="全系列売上累計（ユニレベルマップ）" value={`￥ ${numberFormat(totalEarnings)}`} bold />}
          {targetPositionId === selectedPositionId && <InputFieldConfirm label="当月登録件数（ユニレベルマップ）" value={`${numberFormat(registrationCountThisMonth)} 人`} bold />}
        </section>
        <section className="mx-4 my-5">
          {uniLevelListChildren.map(({ amount, referredPositionId, registrationCountThisMonth, serialAmount, titleHolders, titleName }, index) => (
            <Card body className="border-0 mt-5" key={index}>
              <div className="mt-2">
                <div className="fw-bold">コネクターID</div>
                <hr className="my-2" />
                <div>{referredPositionId ?? '-'}</div>
              </div>
              <div className="mt-4">
                <div className="fw-bold">タイトル</div>
                <hr className="my-2" />
                <div>{titleName ?? '-'}</div>
              </div>
              <div className="mt-4">
                <div className="fw-bold">当月系列課金（ユニレベルマップ）</div>
                <div className="font-13">※ディール代金は含みません</div>
                <hr className="my-2" />
                <div>￥ {numberFormat(serialAmount)}</div>
              </div>
              <div className="mt-4">
                <div className="fw-bold">当月登録件数（ユニレベルマップ）</div>
                <hr className="my-2" />
                <div>{numberFormat(registrationCountThisMonth)} 人</div>
              </div>
              <div className="mt-4">
                <div className="fw-bold">タイトル保有者数</div>
                {titleHolders.map(({ titleName, count }, index) => (
                  <div className="d-flex justify-content-between py-2 border-bottom" key={index}>
                    <div>{titleName}</div>
                    <div>{numberFormat(count)}人</div>
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <Button variant="primary" className="w-100 py-4" onClick={() => navigate(`/referrerManagementUniLevelList/${positionNumber}/${referredPositionId}/1`)}>
                  さらに下層のリスト表示
                </Button>
              </div>
            </Card>
          ))}
          {uniLevelListChildren.length === 0 && (
            <Card body className="border-0">
              <div className="text-center">紹介はありません</div>
            </Card>
          )}
          {targetPositionId !== selectedPositionId && (
            <>
              <div className="mt-5 text-center">
                <Button variant="link" onClick={() => navigate(-1)}>
                  一つ前の表示に戻る
                </Button>
              </div>
              <div className="mt-3 text-center">
                <Button variant="link" onClick={() => navigate(`/referrerManagementUniLevelList/${positionNumber}/0/1`)}>
                  最初に戻る
                </Button>
              </div>
            </>
          )}
          <Pagination
            className="mt-5"
            current={pagination}
            max={maxCount}
            limit={2}
            onClick={(page) => navigate(`/referrerManagementUniLevelList/${positionNumber}/${positionId}/${page}`)}
          />
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
